import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { todayIsoDate } from '@/lib/formatters';
import type { Database, VendorType } from '@/types/database';
import type { BadgeProps } from '@/components/ui/Badge';

export type Vendor = Database['public']['Tables']['vendors']['Row'];

/** Display labels for `vendors.vendor_type`, in dropdown order. */
export const VENDOR_TYPE_LABELS: Record<VendorType, string> = {
  part_vendor: 'Part Vendor',
  raw_materials: 'Raw Materials',
  packaging: 'Packaging',
  contract_manufacturer: 'Contract Manufacturer',
  professional_service: 'Professional Service',
  online_service: 'Online Service',
  equipment: 'Equipment',
  logistics_freight: 'Logistics & Freight',
  utilities: 'Utilities',
  other: 'Other',
};

export const VENDOR_TYPES = Object.keys(VENDOR_TYPE_LABELS) as VendorType[];

/**
 * Every vendor (supplier) in the org, A→Z. Unlike the `useVendors`
 * hook in `features/financials/hooks.ts` (which is gated behind
 * financials read access — super_admin only), this one rides on the
 * `vendors_read_org` RLS policy alone, so anyone with procurement
 * read access can browse the supplier directory.
 */
export function useVendors() {
  return useQuery({
    queryKey: ['vendors'],
    queryFn: async () => {
      const { data, error } = await supabase.from('vendors').select('*').order('name');
      if (error) throw error;
      return data as Vendor[];
    },
  });
}

export interface CreateVendorInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  notes?: string | null;
  vendor_type?: VendorType | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  supplies?: string | null;
  contact_name?: string | null;
  contact_owner?: string | null;
}

/**
 * Insert a new vendor (supplier) row scoped to the caller's org. RLS
 * (`vendors_write_admin`) restricts this to admin / super_admin, so
 * the UI gates the trigger on `canWrite(role, 'procurement')` to
 * avoid a confusing "permission denied" round-trip for other roles.
 */
export function useCreateVendor() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateVendorInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');

      const name = input.name.trim();
      if (!name) throw new Error('Name is required.');

      const { data, error } = await supabase
        .from('vendors')
        .insert({
          organization_id: profile.organization_id,
          name,
          email: input.email?.trim() || null,
          phone: input.phone?.trim() || null,
          website: input.website?.trim() || null,
          notes: input.notes?.trim() || null,
          vendor_type: input.vendor_type ?? null,
          address_line1: input.address_line1?.trim() || null,
          address_line2: input.address_line2?.trim() || null,
          city: input.city?.trim() || null,
          state: input.state?.trim() || null,
          zip_code: input.zip_code?.trim() || null,
          supplies: input.supplies?.trim() || null,
          contact_name: input.contact_name?.trim() || null,
          contact_owner: input.contact_owner?.trim() || null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as Vendor;
    },
    onSuccess: (created) => {
      // Seed the cache so a caller that selects the new vendor right away
      // (the PO form) can render its name before the refetch lands.
      qc.setQueryData<Vendor[]>(['vendors'], (prev) =>
        prev ? [...prev, created].sort((a, b) => a.name.localeCompare(b.name)) : prev,
      );
      qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export interface UpdateVendorInput extends CreateVendorInput {
  id: string;
  is_active?: boolean;
}

/**
 * Update an existing vendor (supplier). Same RLS gate as create
 * (`vendors_write_admin`). Every edit flips `qb_sync_status` back to
 * 'pending' so the Suppliers page "QB Status" badge shows the row has
 * local changes QuickBooks hasn't seen — and so the hourly `qbo-sync`
 * pull knows to leave the row alone until it's pushed.
 */
export function useUpdateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateVendorInput) => {
      const name = input.name.trim();
      if (!name) throw new Error('Name is required.');

      const { data, error } = await supabase
        .from('vendors')
        .update({
          name,
          email: input.email?.trim() || null,
          phone: input.phone?.trim() || null,
          website: input.website?.trim() || null,
          notes: input.notes?.trim() || null,
          vendor_type: input.vendor_type ?? null,
          address_line1: input.address_line1?.trim() || null,
          address_line2: input.address_line2?.trim() || null,
          city: input.city?.trim() || null,
          state: input.state?.trim() || null,
          zip_code: input.zip_code?.trim() || null,
          supplies: input.supplies?.trim() || null,
          contact_name: input.contact_name?.trim() || null,
          contact_owner: input.contact_owner?.trim() || null,
          ...(input.is_active !== undefined ? { is_active: input.is_active } : {}),
          qb_sync_status: 'pending',
        })
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Vendor;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

/**
 * Push one vendor to QuickBooks via the `qbo-vendor-push` edge function —
 * creates the QBO Vendor if the row has no `qb_external_id` yet, otherwise
 * sparse-updates the existing one. On success the function stamps
 * `qb_sync_status = 'synced'`, which the invalidation below picks up.
 */
export function usePushVendorToQbo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vendorId: string) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        qb_external_id?: string;
        error?: string;
      }>('qbo-vendor-push', { body: { vendor_id: vendorId } });
      if (error) {
        // supabase-js wraps non-2xx responses in a FunctionsHttpError whose
        // context carries our jsonResponse body — surface that message.
        const ctx = (error as { context?: Response }).context;
        if (ctx) {
          const body = (await ctx.json().catch(() => null)) as { error?: string } | null;
          if (body?.error) throw new Error(body.error);
        }
        throw error;
      }
      if (!data?.ok) throw new Error(data?.error ?? 'Push to QuickBooks failed.');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

// -----------------------------------------------------------------------
// Purchase orders (migration 104)
//
// Lifecycle: internal_po → ordered → in_production → shipped →
// partially_received → delivered. `delivered` is only reachable once
// receiving forms cover every line — the DB trigger rejects it otherwise,
// and receiving advances the status automatically.
// -----------------------------------------------------------------------

export const PO_STATUSES = [
  'internal_po',
  'ordered',
  'in_production',
  'shipped',
  'partially_received',
  'delivered',
  'cancelled',
] as const;
export type PoStatus = (typeof PO_STATUSES)[number];

export const PO_STATUS_LABELS: Record<PoStatus, string> = {
  internal_po: 'Internal PO made',
  ordered: 'Order placed',
  in_production: 'Vendor production',
  shipped: 'On the way',
  partially_received: 'Partially received',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const PO_STATUS_VARIANTS: Record<PoStatus, BadgeVariant> = {
  internal_po: 'muted',
  ordered: 'secondary',
  in_production: 'outline',
  shipped: 'info',
  partially_received: 'warning',
  delivered: 'success',
  cancelled: 'muted',
};

/** Statuses an operator can pick by hand. Partial / delivered come from receiving. */
export const PO_MANUAL_STATUSES: PoStatus[] = [
  'internal_po',
  'ordered',
  'in_production',
  'shipped',
  'cancelled',
];

/** Statuses that count as "done" and are hidden from the default PO view. */
export const PO_CLOSED_STATUSES: PoStatus[] = ['delivered', 'cancelled'];

/** Statuses that can still be received against. */
export const PO_RECEIVABLE_STATUSES: PoStatus[] = [
  'internal_po',
  'ordered',
  'in_production',
  'shipped',
  'partially_received',
];

export const PURCHASE_METHODS = [
  'card',
  'amex',
  'mercury_card',
  'terms',
  'ach',
  'wire',
  'check',
  'paypal',
  'other',
] as const;
export type PurchaseMethod = (typeof PURCHASE_METHODS)[number];

/**
 * Generic card methods from before POs recorded the specific card. Still
 * valid on existing POs, but new picks go through `card` + a mapped card.
 */
export const LEGACY_CARD_METHODS: PurchaseMethod[] = ['amex', 'mercury_card'];

export const PURCHASE_METHOD_LABELS: Record<PurchaseMethod, string> = {
  card: 'Card',
  amex: 'AMEX',
  mercury_card: 'Mercury card',
  terms: 'Terms (invoice)',
  ach: 'ACH',
  wire: 'Wire',
  check: 'Check',
  paypal: 'PayPal',
  other: 'Other',
};

export const ORDER_METHODS = [
  'website',
  'email',
  'phone',
  'vendor_portal',
  'amazon',
  'in_person',
  'other',
] as const;
export type OrderMethod = (typeof ORDER_METHODS)[number];

export const ORDER_METHOD_LABELS: Record<OrderMethod, string> = {
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
  vendor_portal: 'Vendor portal',
  amazon: 'Amazon',
  in_person: 'In person',
  other: 'Other',
};

/** A company card from Card mapping, as exposed by `list_purchase_cards()`. */
export interface PurchaseCard {
  id: string;
  name_on_card: string | null;
  last_four: string | null;
  network: string | null;
  status: string | null;
  account_name: string | null;
  assignee_names: string[];
}

const INACTIVE_CARD_STATUSES = ['cancelled', 'expired'];

export function isSelectablePurchaseCard(card: PurchaseCard): boolean {
  return !INACTIVE_CARD_STATUSES.includes(card.status ?? '');
}

/** "Jane Doe •••• 1234" */
export function purchaseCardLabel(card: PurchaseCard | null | undefined): string {
  if (!card) return 'Card';
  return `${card.name_on_card ?? 'Card'} •••• ${card.last_four ?? '????'}`;
}

/**
 * Cards available on Card mapping, for the PO purchase-method picker. Goes
 * through a security-definer RPC because `cards` RLS hides them from the
 * admins / managers who work with POs.
 */
export function usePurchaseCards() {
  return useQuery({
    queryKey: ['purchase-cards'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_purchase_cards' as never);
      if (error) throw error;
      return (data ?? []) as unknown as PurchaseCard[];
    },
  });
}

/** Display label for a PO's purchase method, naming the card when one is recorded. */
export function purchaseMethodLabel(
  method: PurchaseMethod | null | undefined,
  cardId: string | null | undefined,
  cards: PurchaseCard[] | undefined,
): string | null {
  if (!method) return null;
  if (method === 'card') return purchaseCardLabel(cards?.find((c) => c.id === cardId));
  return PURCHASE_METHOD_LABELS[method];
}

type BadgeVariant = NonNullable<BadgeProps['variant']>;

export interface PurchaseOrderLine {
  id: string;
  purchase_order_id: string;
  /** Null when the line is a one-off custom item that is not in the catalog. */
  item_id: string | null;
  description: string | null;
  /** Unit the line is ordered, priced, and received in (e.g. "roll"). */
  uom: string;
  /** Stock units (items.base_uom) in one `uom`; 1 when ordered in the stock unit. */
  conversion_to_base: number;
  quantity: number;
  received_quantity: number;
  unit_price_cents: number;
  /** quantity × unit_price_cents; the tariff is not in it. */
  line_total_cents: number;
  /** Tariff / duty for the whole line, in cents (migration 156). */
  tariff_cents: number;
  /** This line's even share of the PO's shipping charges, whole line, in cents (migration 157). */
  shipping_alloc_cents: number;
  /** This line's even share of the PO's other charges (fees, surcharges…), whole line, in cents. */
  other_charges_alloc_cents: number;
  order_method: OrderMethod | null;
  external_order_ref: string | null;
  /** The vendor's own part number for this line (migration 151). */
  supplier_part_number: string | null;
  position: number;
  items: {
    id: string;
    sku: string;
    name: string;
    base_uom: string;
    is_lot_tracked: boolean;
  } | null;
}

/** Catalog name, or the free-text description of a one-off line. */
export function poLineName(line: {
  description?: string | null;
  items?: { name?: string | null; sku?: string | null } | null;
}): string {
  return line.items?.name || line.description?.trim() || line.items?.sku || 'Custom item';
}

/** Name shown for a received line, including a custom PO line with no catalog item. */
export function receivingLineName(line: {
  items?: { name?: string | null; sku?: string | null } | null;
  po_lines?: { description?: string | null } | null;
}): string {
  return line.items?.name || line.po_lines?.description?.trim() || line.items?.sku || 'Custom item';
}

/** Common places an order gets placed — suggestions for `order_platform` (free text). */
export const ORDER_PLATFORM_SUGGESTIONS = [
  'Alibaba',
  'Amazon',
  'AliExpress',
  'Made-in-China',
  'Global Sources',
  'Vendor website',
  'Vendor portal',
  'Email',
  'Phone',
] as const;

/** What a charge at the bottom of a PO is for. Shipping rolls up separately from the rest. */
export const PO_CHARGE_KINDS = [
  'shipping',
  'fuel_surcharge',
  'service_fee',
  'handling',
  'customs_brokerage',
  'insurance',
  'other',
] as const;

export type PoChargeKind = (typeof PO_CHARGE_KINDS)[number];

export const PO_CHARGE_KIND_LABELS: Record<PoChargeKind, string> = {
  shipping: 'Shipping',
  fuel_surcharge: 'Fuel surcharge',
  service_fee: 'Service fee',
  handling: 'Handling',
  customs_brokerage: 'Customs / brokerage',
  insurance: 'Insurance',
  other: 'Other',
};

export interface PoShippingCharge {
  id: string;
  purchase_order_id: string;
  /** Shipping, or one of the fee kinds (migration 156). */
  kind: PoChargeKind;
  description: string | null;
  /** Invoice / booking / tracking reference. */
  reference: string | null;
  amount_cents: number;
  charged_at: string | null;
  position: number;
}

/** One tracking number on a PO (migration 157); a PO shipped in parts has several. */
export interface PoShipment {
  id: string;
  purchase_order_id: string;
  carrier: string | null;
  tracking_number: string;
  /** What is in this parcel when a PO ships in parts, e.g. "Motors". */
  description: string | null;
  tracking_status: TrackingStatus | null;
  tracking_estimated_delivery: string | null;
  tracking_last_event: string | null;
  tracking_last_location: string | null;
  tracking_checked_at: string | null;
  tracking_provider_id: string | null;
  tracking_url: string | null;
  tracking_error: string | null;
  position: number;
}

export type PoAttachmentKind = 'order_receipt' | 'shipping' | 'other';

export interface PoAttachment {
  id: string;
  organization_id: string;
  purchase_order_id: string;
  shipping_charge_id: string | null;
  kind: PoAttachmentKind;
  file_name: string;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

export interface PurchaseOrder {
  id: string;
  organization_id: string;
  po_number: string;
  vendor_id: string;
  status: PoStatus;
  purchase_method: PurchaseMethod | null;
  purchase_card_id: string | null;
  payment_terms: string | null;
  /** Where the external order was placed, e.g. "Alibaba". */
  order_platform: string | null;
  /** Order # on that platform. */
  order_reference: string | null;
  /** Date (YYYY-MM-DD) the external order was placed. */
  ordered_at: string | null;
  /** Expected delivery date (YYYY-MM-DD). */
  expected_at: string | null;
  production_lead_time_days: number | null;
  shipping_lead_time_days: number | null;
  /** Total lead time (production + shipping). */
  lead_time_days: number | null;
  ship_to_location_id: string | null;
  subtotal_cents: number;
  /** Rollup of `po_lines.tariff_cents`. */
  tariff_cents: number;
  tax_cents: number;
  /** Rollup of `po_shipping_charges` with kind `shipping`. */
  shipping_cents: number;
  /** Rollup of every other charge kind (fuel surcharges, service fees…). */
  other_charges_cents: number;
  processing_fee_cents: number;
  total_cents: number;
  carrier: string | null;
  /**
   * Roll-up of `po_shipments` (migration 157): the first shipment's number and
   * carrier, the least-advanced carrier status, and the latest ETA among the
   * parcels still on the way. Edit shipments, not these.
   */
  tracking_number: string | null;
  tracking_status: TrackingStatus | null;
  tracking_estimated_delivery: string | null;
  tracking_last_event: string | null;
  tracking_last_location: string | null;
  tracking_checked_at: string | null;
  tracking_provider_id: string | null;
  tracking_url: string | null;
  tracking_error: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  vendors: { id: string; name: string } | null;
  ship_to: { id: string; name: string; code: string } | null;
  po_lines: PurchaseOrderLine[];
  po_shipping_charges: PoShippingCharge[];
  po_shipments: PoShipment[];
  po_attachments: PoAttachment[];
}

const PO_SELECT =
  '*, vendors:vendor_id(id, name), ship_to:ship_to_location_id(id, name, code), po_lines(*, items(id, sku, name, base_uom, is_lot_tracked)), po_shipping_charges!po_shipping_charges_purchase_order_id_fkey(*), po_shipments!po_shipments_purchase_order_id_fkey(*), po_attachments!po_attachments_purchase_order_id_fkey(*)';

function sortLines(po: PurchaseOrder): PurchaseOrder {
  return {
    ...po,
    po_lines: [...(po.po_lines ?? [])].sort((a, b) => a.position - b.position),
    po_shipping_charges: [...(po.po_shipping_charges ?? [])].sort(
      (a, b) => a.position - b.position,
    ),
    po_shipments: [...(po.po_shipments ?? [])].sort((a, b) => a.position - b.position),
    po_attachments: [...(po.po_attachments ?? [])].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    ),
  };
}

/** Landed cost of a PO line: goods + tariff + its share of shipping and other charges. */
export function poLineLandedCents(
  line: Pick<
    PurchaseOrderLine,
    'line_total_cents' | 'tariff_cents' | 'shipping_alloc_cents' | 'other_charges_alloc_cents'
  >,
): number {
  return (
    line.line_total_cents +
    (line.tariff_cents ?? 0) +
    (line.shipping_alloc_cents ?? 0) +
    (line.other_charges_alloc_cents ?? 0)
  );
}

/** Every purchase order in the org with its vendor and lines, newest first. */
export function usePurchaseOrders() {
  return useQuery({
    queryKey: ['purchase-orders'],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('purchase_orders' as never)
        .select(PO_SELECT)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as unknown as PurchaseOrder[]).map(sortLines);
    },
  });
}

export const TRACKING_STATUS_LABELS = {
  unknown: 'Tracking added',
  pre_transit: 'Label created',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  available_for_pickup: 'Ready for pickup',
  delivered: 'Carrier delivered',
  return_to_sender: 'Returning to sender',
  failure: 'Delivery issue',
  cancelled: 'Shipment cancelled',
  error: 'Tracking error',
} as const;

export type TrackingStatus = keyof typeof TRACKING_STATUS_LABELS;

/** Carrier page for a shipment (or the PO header roll-up): the provider's URL, else a carrier guess. */
export function purchaseOrderTrackingUrl(po: {
  tracking_url?: string | null;
  tracking_number: string | null;
  carrier: string | null;
}): string | null {
  if (po.tracking_url) return po.tracking_url;
  if (!po.tracking_number) return null;
  const code = encodeURIComponent(po.tracking_number);
  const carrier = (po.carrier ?? '').toLowerCase();
  if (carrier.includes('ups')) return `https://www.ups.com/track?tracknum=${code}`;
  if (carrier.includes('fedex') || carrier.includes('fed ex')) {
    return `https://www.fedex.com/fedextrack/?trknbr=${code}`;
  }
  if (carrier.includes('usps') || carrier.includes('postal')) {
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${code}`;
  }
  if (carrier.includes('dhl')) {
    return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${code}`;
  }
  return null;
}

/** Refresh one carrier record immediately; the scheduled sync runs every 15 minutes. */
export function useRefreshPurchaseOrderTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (purchaseOrderId: string) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        error?: string;
        results?: Array<{ error?: string }>;
      }>('po-tracking-sync', { body: { purchase_order_id: purchaseOrderId } });
      if (error) {
        const context = (error as { context?: Response }).context;
        if (context) {
          const body = (await context
            .clone()
            .json()
            .catch(() => null)) as { error?: string } | null;
          if (body?.error) throw new Error(body.error);
        }
        throw error;
      }
      const resultError = data?.error ?? data?.results?.find((result) => result.error)?.error;
      if (!data?.ok || resultError) throw new Error(resultError ?? 'Tracking refresh failed.');
      return data;
    },
    onSuccess: () => invalidateProcurement(qc),
  });
}

export interface SavePurchaseOrderLineInput {
  id?: string | null;
  /** Null for a one-off custom line. `description` is required in that case. */
  item_id: string | null;
  description?: string | null;
  uom?: string | null;
  /** Stock units per `uom`. Omit on an existing line to keep its current conversion. */
  conversion_to_base?: number | null;
  quantity: number;
  unit_price_cents: number;
  /** Tariff for the whole line in cents. Omit on an existing line to keep its current tariff. */
  tariff_cents?: number | null;
  order_method?: OrderMethod | null;
  external_order_ref?: string | null;
  supplier_part_number?: string | null;
}

export interface SavePurchaseOrderInput {
  id?: string | null;
  po_number?: string | null;
  vendor_id: string;
  status: PoStatus;
  purchase_method?: PurchaseMethod | null;
  purchase_card_id?: string | null;
  payment_terms?: string | null;
  order_platform?: string | null;
  order_reference?: string | null;
  ordered_at?: string | null;
  expected_at?: string | null;
  production_lead_time_days?: number | null;
  shipping_lead_time_days?: number | null;
  lead_time_days?: number | null;
  ship_to_location_id?: string | null;
  tax_cents?: number | null;
  processing_fee_cents?: number | null;
  shipped_at?: string | null;
  notes?: string | null;
  /**
   * Replaces the PO's charges; shipping ones sum into `shipping_cents`, the
   * rest into `other_charges_cents`.
   */
  shipping_charges?: SavePoShippingChargeInput[];
  /**
   * Replaces the PO's tracking numbers. A number already on the PO keeps its
   * carrier state; numbers left out are removed. Omit to leave them alone.
   */
  shipments?: SavePoShipmentInput[];
  lines: SavePurchaseOrderLineInput[];
}

export interface SavePoShipmentInput {
  carrier?: string | null;
  tracking_number: string;
  description?: string | null;
}

export interface SavePoShippingChargeInput {
  /** Existing id, or a client-generated uuid for a new charge (so files can be attached to it). */
  id?: string | null;
  /** Defaults to shipping. */
  kind?: PoChargeKind | null;
  description?: string | null;
  reference?: string | null;
  amount_cents: number;
  charged_at?: string | null;
}

function invalidateProcurement(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['purchase-orders'] });
  // Saving a PO updates the vendor's current price per item (migration 156).
  qc.invalidateQueries({ queryKey: ['vendor-item-terms'] });
  qc.invalidateQueries({ queryKey: ['item-suppliers'] });
  qc.invalidateQueries({ queryKey: ['planning-suppliers'] });
  qc.invalidateQueries({ queryKey: ['supplier-part-numbers'] });
  qc.invalidateQueries({ queryKey: ['receiving-lines'] });
  qc.invalidateQueries({ queryKey: ['item-po-lines'] });
  qc.invalidateQueries({ queryKey: ['item-cost-summaries'] });
  qc.invalidateQueries({ queryKey: ['item-cost-history'] });
  // BOM roll-ups price parts at the latest PO cost (migration 162).
  qc.invalidateQueries({ queryKey: ['bom-graph'] });
  qc.invalidateQueries({ queryKey: ['stock-movements'] });
  qc.invalidateQueries({ queryKey: ['inventory-levels'] });
  qc.invalidateQueries({ queryKey: ['lots'] });
  qc.invalidateQueries({ queryKey: ['returns'] });
  qc.invalidateQueries({ queryKey: ['production-forms'] });
}

/**
 * Create or update a PO with its lines in one transaction via the
 * `save_purchase_order` RPC. Lines carrying an `id` are updated, lines
 * without one are inserted, and existing lines missing from the payload
 * are removed. Removing a line that was already received reverses that
 * receipt and the stock it posted.
 */
export function useSavePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SavePurchaseOrderInput) => {
      const { lines, ...header } = input;
      const { data, error } = await supabase.rpc(
        'save_purchase_order' as never,
        {
          p_po: header,
          p_lines: lines,
        } as never,
      );
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => invalidateProcurement(qc),
  });
}

export interface VendorItemTerms {
  item_id: string;
  /** Per `purchase_uom` when set, otherwise per stock unit. Can be a fraction of a cent. */
  unit_cost_cents: number | null;
  /** Date (YYYY-MM-DD) the price is good for: the PO order date it came from, or the day it was typed in. */
  unit_cost_as_of: string | null;
  /** PO line the price came from; null when it was typed in by hand. */
  unit_cost_po_line_id: string | null;
  lead_time_days: number | null;
  supplier_part_number: string | null;
  purchase_uom: string | null;
  purchase_uom_size: number | null;
  purchase_uom_size_uom: string | null;
  units_per_purchase_uom: number;
}

/** A vendor's quoted cost, lead time, and part number per item (from item_suppliers), keyed by item id. */
export function useVendorItemTerms(vendorId: string | null | undefined) {
  return useQuery({
    queryKey: ['vendor-item-terms', vendorId],
    enabled: !!vendorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .select(
          'item_id, unit_cost_cents, unit_cost_as_of, unit_cost_po_line_id, lead_time_days, supplier_part_number, purchase_uom, purchase_uom_size, purchase_uom_size_uom, units_per_purchase_uom',
        )
        .eq('vendor_id', vendorId!);
      if (error) throw error;
      const map = new Map<string, VendorItemTerms>();
      for (const row of data as unknown as VendorItemTerms[]) {
        map.set(row.item_id, {
          ...row,
          unit_cost_cents: row.unit_cost_cents == null ? null : Number(row.unit_cost_cents),
          purchase_uom_size: row.purchase_uom_size == null ? null : Number(row.purchase_uom_size),
          units_per_purchase_uom: Number(row.units_per_purchase_uom ?? 1) || 1,
        });
      }
      return map;
    },
  });
}

/**
 * Whether saving a PO dated `orderedAt` (blank = today) makes a line's price
 * the vendor's current price for the item. Mirrors
 * `sync_item_supplier_prices_from_po`: a price from a newer PO, or one typed
 * in more recently, stays unless this very line set it.
 */
export function poPriceBecomesCurrent(
  terms: VendorItemTerms | undefined,
  orderedAt: string | null | undefined,
  lineId: string | null | undefined,
): boolean {
  if (!terms || terms.unit_cost_cents == null || !terms.unit_cost_as_of) return true;
  if (lineId && terms.unit_cost_po_line_id === lineId) return true;
  return terms.unit_cost_as_of <= (orderedAt || todayIsoDate());
}

export interface SupplierPartNumber {
  item_id: string;
  vendor_id: string;
  supplier_part_number: string;
}

/** Every vendor's part number for each item, keyed by item id — lets item search match them. */
export function useSupplierPartNumbers() {
  return useQuery({
    queryKey: ['supplier-part-numbers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .select('item_id, vendor_id, supplier_part_number')
        .not('supplier_part_number', 'is', null);
      if (error) throw error;
      const map = new Map<string, SupplierPartNumber[]>();
      for (const row of data as unknown as SupplierPartNumber[]) {
        if (!row.supplier_part_number.trim()) continue;
        const list = map.get(row.item_id) ?? [];
        list.push(row);
        map.set(row.item_id, list);
      }
      return map;
    },
  });
}

/** Quick status change from the PO table (e.g. Ordered → Shipped). */
export function useUpdatePurchaseOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: PoStatus }) => {
      const { error } = await supabase
        .from('purchase_orders' as never)
        .update({ status: input.status } as never)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateProcurement(qc),
  });
}

// -----------------------------------------------------------------------
// PO attachments (migration 117) — order receipts and shipping invoices.
// Bytes live in the private `purchase-order-files` bucket under
// `<organization_id>/<purchase_order_id>/<filename>`.
// -----------------------------------------------------------------------

const PO_FILES_BUCKET = 'purchase-order-files';

export interface UploadPoAttachmentInput {
  purchaseOrderId: string;
  file: File;
  kind: PoAttachmentKind;
  shippingChargeId?: string | null;
}

/** Upload a file to the PO bucket and register it; rolls back the object if the row insert fails. */
export function useUploadPoAttachment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: UploadPoAttachmentInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const cleaned = input.file.name.replace(/[^a-zA-Z0-9._-]+/g, '_');
      const path = `${profile.organization_id}/${input.purchaseOrderId}/${Date.now()}-${cleaned}`;

      const { error: uploadErr } = await supabase.storage
        .from(PO_FILES_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const { data, error: dbErr } = await supabase
        .from('po_attachments' as never)
        .insert({
          organization_id: profile.organization_id,
          purchase_order_id: input.purchaseOrderId,
          shipping_charge_id: input.shippingChargeId ?? null,
          kind: input.kind,
          file_name: input.file.name,
          storage_path: path,
          mime_type: input.file.type || null,
          size_bytes: input.file.size,
          uploaded_by: profile.id,
        } as never)
        .select('*')
        .single();
      if (dbErr) {
        await supabase.storage.from(PO_FILES_BUCKET).remove([path]);
        throw dbErr;
      }
      return data as unknown as PoAttachment;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
}

/** Delete an attachment row, then its stored object. */
export function useDeletePoAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachment: Pick<PoAttachment, 'id' | 'storage_path'>) => {
      const { error } = await supabase
        .from('po_attachments' as never)
        .delete()
        .eq('id', attachment.id);
      if (error) throw error;
      await supabase.storage.from(PO_FILES_BUCKET).remove([attachment.storage_path]);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
}

/**
 * Remove stored objects whose rows were already cascaded away (e.g. the
 * shipping charge they belonged to was deleted). Best effort.
 */
export async function removePoFiles(paths: string[]) {
  if (paths.length === 0) return;
  await supabase.storage.from(PO_FILES_BUCKET).remove(paths);
}

/** Open a PO attachment in a new tab via a five-minute signed URL. */
export async function openPoAttachment(attachment: Pick<PoAttachment, 'storage_path'>) {
  // Open synchronously so popup blockers treat it as user-initiated.
  const win = window.open('', '_blank');
  if (win) win.opener = null;
  const { data, error } = await supabase.storage
    .from(PO_FILES_BUCKET)
    .createSignedUrl(attachment.storage_path, 60 * 5);
  if (error) {
    win?.close();
    throw error;
  }
  if (win) win.location.href = data.signedUrl;
  else window.location.assign(data.signedUrl);
}

// -----------------------------------------------------------------------
// Receiving (goods receipts)
// -----------------------------------------------------------------------

export const RECEIPT_STATUSES = ['posted', 'partially_reversed', 'reversed'] as const;
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  posted: 'Posted',
  partially_reversed: 'Partially reversed',
  reversed: 'Reversed',
};

/** One received line — the unit the Receiving table lists. */
export interface ReceivingLineRow {
  id: string;
  /** In the PO line's `uom`. */
  quantity: number;
  /** Stock units per received unit, frozen when the receipt posted. */
  conversion_to_base: number;
  /** Stock units this receipt added to on-hand. */
  base_quantity: number;
  /** Per received unit (`po_lines.uom`). */
  unit_cost_cents: number | null;
  notes: string | null;
  item_id: string | null;
  po_line_id: string | null;
  to_location_id: string | null;
  lot_id: string | null;
  status: ReceiptStatus | 'posted';
  items: { id: string; sku: string; name: string; base_uom: string } | null;
  locations: { id: string; name: string; code: string } | null;
  lots: { lot_number: string } | null;
  po_lines: {
    quantity: number;
    received_quantity: number;
    unit_price_cents: number;
    description: string | null;
    uom: string;
    conversion_to_base: number;
  } | null;
  goods_receipts: {
    id: string;
    receipt_number: string | null;
    received_at: string;
    packing_slip_number: string | null;
    notes: string | null;
    status: ReceiptStatus | 'posted';
    purchase_orders: {
      id: string;
      po_number: string;
      status: PoStatus;
      vendors: { id: string; name: string } | null;
    } | null;
  } | null;
}

export function useReceivingLines() {
  return useQuery({
    queryKey: ['receiving-lines'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gr_lines' as never)
        .select(
          'id, quantity, conversion_to_base, base_quantity, unit_cost_cents, notes, item_id, po_line_id, to_location_id, lot_id, status, items(id, sku, name, base_uom), locations:to_location_id(id, name, code), lots(lot_number), po_lines(quantity, received_quantity, unit_price_cents, description, uom, conversion_to_base), goods_receipts!inner(id, receipt_number, received_at, packing_slip_number, notes, status, purchase_orders(id, po_number, status, vendors:vendor_id(id, name)))',
        )
        .limit(2000);
      if (error) throw error;
      return (data as unknown as ReceivingLineRow[]).sort((a, b) =>
        (b.goods_receipts?.received_at ?? '').localeCompare(a.goods_receipts?.received_at ?? ''),
      );
    },
  });
}

export interface ReceiveLineInput {
  po_line_id: string;
  quantity: number;
  to_location_id: string;
  unit_cost_cents?: number | null;
  lot_number?: string | null;
  notes?: string | null;
}

export interface ReceivePurchaseOrderInput {
  purchaseOrderId: string;
  receivedAt: string;
  packingSlipNumber?: string | null;
  notes?: string | null;
  lines: ReceiveLineInput[];
}

/**
 * Post a receiving form against a PO via `receive_purchase_order`. Each
 * line becomes a gr_lines row whose trigger records a 'receipt' stock
 * movement (raising on-hand), bumps the PO line's received quantity,
 * and advances the PO to partially received / delivered.
 */
export function useReceivePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReceivePurchaseOrderInput) => {
      const { data, error } = await supabase.rpc(
        'receive_purchase_order' as never,
        {
          p_po_id: input.purchaseOrderId,
          p_received_at: input.receivedAt,
          p_packing_slip_number: input.packingSlipNumber ?? null,
          p_notes: input.notes ?? null,
          p_lines: input.lines,
        } as never,
      );
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => {
      invalidateProcurement(qc);
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}

// -----------------------------------------------------------------------
// Item costs (from receiving)
// -----------------------------------------------------------------------

/**
 * Costs are per stock unit (items.base_uom), whatever unit the item was bought
 * in. `last_cost_cents` / `avg_cost_12m_cents` are the landed cost (item
 * price + tariff + the line's even share of the PO's shipping and other
 * charges); the component columns say what makes it up (migration 157).
 */
export interface ItemCostSummary {
  item_id: string;
  last_cost_cents: number | null;
  last_received_at: string | null;
  avg_cost_12m_cents: number | null;
  qty_received_12m: number;
  receipt_count_12m: number;
  /** Unrounded cents per stock unit, e.g. 2.5 for a $45 roll of 1,800 in. */
  last_cost_cents_exact: number | null;
  avg_cost_12m_cents_exact: number | null;
  /** What the vendor charged for the item alone — what a new PO line should start from. */
  last_goods_cost_cents_exact: number | null;
  last_tariff_cents_exact: number | null;
  last_shipping_cents_exact: number | null;
  last_other_charges_cents_exact: number | null;
  avg_goods_cost_12m_cents_exact: number | null;
  avg_tariff_12m_cents_exact: number | null;
  avg_shipping_12m_cents_exact: number | null;
  avg_other_charges_12m_cents_exact: number | null;
}

/** The pieces of a landed cost, per stock unit, in (possibly fractional) cents. */
export interface LandedCostParts {
  goods: number;
  tariff: number;
  shipping: number;
  other: number;
  landed: number;
}

/** Most recent receipt's landed cost split into its parts; null before the first receipt. */
export function lastLandedCostParts(
  summary: ItemCostSummary | null | undefined,
): LandedCostParts | null {
  if (!summary || summary.last_cost_cents_exact == null) return null;
  const goods = Number(summary.last_goods_cost_cents_exact ?? summary.last_cost_cents_exact);
  const tariff = Number(summary.last_tariff_cents_exact ?? 0);
  const shipping = Number(summary.last_shipping_cents_exact ?? 0);
  const other = Number(summary.last_other_charges_cents_exact ?? 0);
  return { goods, tariff, shipping, other, landed: Number(summary.last_cost_cents_exact) };
}

/** Trailing-12-month average landed cost split into its parts; null without receipts in range. */
export function avgLandedCostParts(
  summary: ItemCostSummary | null | undefined,
): LandedCostParts | null {
  if (!summary || summary.avg_cost_12m_cents_exact == null) return null;
  const goods = Number(summary.avg_goods_cost_12m_cents_exact ?? summary.avg_cost_12m_cents_exact);
  const tariff = Number(summary.avg_tariff_12m_cents_exact ?? 0);
  const shipping = Number(summary.avg_shipping_12m_cents_exact ?? 0);
  const other = Number(summary.avg_other_charges_12m_cents_exact ?? 0);
  return { goods, tariff, shipping, other, landed: Number(summary.avg_cost_12m_cents_exact) };
}

/** The vendor's item price per stock unit from the last receipt, before tariffs and freight. */
export function lastGoodsCostCents(summary: ItemCostSummary | null | undefined): number | null {
  if (!summary) return null;
  const exact = summary.last_goods_cost_cents_exact ?? summary.last_cost_cents_exact;
  if (exact != null) return Number(exact);
  return summary.last_cost_cents;
}

/** Most-recent and trailing-12-month average cost for every received item, keyed by item id. */
export function useItemCostSummaries() {
  return useQuery({
    queryKey: ['item-cost-summaries'],
    queryFn: async () => {
      const { data, error } = await supabase.from('item_cost_summary' as never).select('*');
      if (error) throw error;
      const map = new Map<string, ItemCostSummary>();
      for (const row of data as unknown as ItemCostSummary[]) map.set(row.item_id, row);
      return map;
    },
  });
}

export function useItemCostSummary(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-cost-summaries', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_cost_summary' as never)
        .select('*')
        .eq('item_id', itemId!)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as ItemCostSummary | null) ?? null;
    },
  });
}

export type ItemCostSource = 'po' | 'supplier' | 'production';

/**
 * The cost of one UoM Produced (items.base_uom) that BOMs, production,
 * scrap and inventory value use (migration 162): landed cost of the most
 * recent PO line, else the supplier's entered price ÷ units per purchase
 * unit, else the latest production run for built items.
 */
export interface ItemCurrentCost {
  item_id: string;
  cost_source: ItemCostSource;
  unit_cost_cents: number;
  as_of: string | null;
  vendor_id: string | null;
  po_line_id: string | null;
  purchase_order_id: string | null;
  po_number: string | null;
  /** Per UoM Produced; only set when `cost_source` is 'po'. */
  goods_cents: number | null;
  tariff_cents: number | null;
  shipping_cents: number | null;
  other_charges_cents: number | null;
}

function normalizeCurrentCost(row: ItemCurrentCost): ItemCurrentCost {
  const num = (v: number | null) => (v == null ? null : Number(v));
  return {
    ...row,
    unit_cost_cents: Number(row.unit_cost_cents),
    goods_cents: num(row.goods_cents),
    tariff_cents: num(row.tariff_cents),
    shipping_cents: num(row.shipping_cents),
    other_charges_cents: num(row.other_charges_cents),
  };
}

/** Current cost for every item that has one, keyed by item id. */
export function useItemCurrentCosts() {
  return useQuery({
    queryKey: ['item-cost-summaries', 'current'],
    queryFn: async () => {
      const { data, error } = await supabase.from('item_current_cost' as never).select('*');
      if (error) throw error;
      const map = new Map<string, ItemCurrentCost>();
      for (const row of data as unknown as ItemCurrentCost[]) {
        map.set(row.item_id, normalizeCurrentCost(row));
      }
      return map;
    },
  });
}

export function useItemCurrentCost(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-cost-summaries', 'current', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_current_cost' as never)
        .select('*')
        .eq('item_id', itemId!)
        .maybeSingle();
      if (error) throw error;
      return data ? normalizeCurrentCost(data as unknown as ItemCurrentCost) : null;
    },
  });
}

/** "Landed on PO-0002", "Supplier price (no PO yet)", "Last production run". */
export function itemCostSourceLabel(cost: Pick<ItemCurrentCost, 'cost_source' | 'po_number'>): string {
  if (cost.cost_source === 'po') return `Landed on ${cost.po_number ?? 'the latest PO'}`;
  if (cost.cost_source === 'supplier') return 'Supplier price (no PO yet)';
  return 'Last production run';
}

export interface ItemCostHistoryRow {
  id: string;
  /** In the PO line's `uom`. */
  quantity: number;
  conversion_to_base: number;
  base_quantity: number;
  /** Item price per received unit (`po_lines.uom`), before tariffs and freight. */
  unit_cost_cents: number | null;
  /** The PO line's whole-line extras, spread over its ordered quantity to land this receipt. */
  po_lines: {
    uom: string;
    quantity: number;
    tariff_cents: number;
    shipping_alloc_cents: number;
    other_charges_alloc_cents: number;
  } | null;
  status?: ReceiptStatus | 'posted';
  goods_receipts: {
    receipt_number: string | null;
    received_at: string;
    purchase_orders: {
      id: string;
      po_number: string;
      purchase_method: PurchaseMethod | null;
      purchase_card_id: string | null;
      vendors: { id: string; name: string } | null;
    } | null;
  } | null;
}

/** Every receipt of one item with the cost paid, newest first. */
export function useItemCostHistory(itemId: string | undefined) {
  return useQuery({
    queryKey: ['item-cost-history', itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gr_lines' as never)
        .select(
          'id, quantity, conversion_to_base, base_quantity, unit_cost_cents, status, po_lines(uom, quantity, tariff_cents, shipping_alloc_cents, other_charges_alloc_cents), goods_receipts!inner(receipt_number, received_at, status, purchase_orders(id, po_number, purchase_method, purchase_card_id, vendors:vendor_id(id, name)))',
        )
        .eq('item_id', itemId!)
        .limit(500);
      if (error) throw error;
      return (data as unknown as ItemCostHistoryRow[]).sort((a, b) =>
        (b.goods_receipts?.received_at ?? '').localeCompare(a.goods_receipts?.received_at ?? ''),
      );
    },
  });
}

/**
 * Landed cost of one receipt line per received unit (`po_lines.uom`), the
 * same way `item_cost_summary` lands it: the PO line's tariff and charge
 * shares are spread over the line's ordered quantity. Null when the receipt
 * has no cost.
 */
export function receiptLandedCostParts(
  row: Pick<ItemCostHistoryRow, 'unit_cost_cents' | 'po_lines'>,
): LandedCostParts | null {
  if (row.unit_cost_cents == null) return null;
  const line = row.po_lines;
  const ordered = Number(line?.quantity ?? 0);
  const perUnit = (cents: number | null | undefined) =>
    line && ordered > 0 ? Number(cents ?? 0) / ordered : 0;
  const goods = Number(row.unit_cost_cents);
  const tariff = perUnit(line?.tariff_cents);
  const shipping = perUnit(line?.shipping_alloc_cents);
  const other = perUnit(line?.other_charges_alloc_cents);
  return { goods, tariff, shipping, other, landed: goods + tariff + shipping + other };
}

export function useVendorInvoices() {
  return useQuery({
    queryKey: ['vendor-invoices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_invoices' as never)
        .select('*, vendors(name), purchase_orders(po_number)')
        .order('invoice_date', { ascending: false });
      if (error) throw error;
      return data as Array<Record<string, unknown>>;
    },
  });
}
